import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit, inject } from '@angular/core';
import { Router } from '@angular/router';
import { BrightsideUiModule, HeaderLabelCellConfig } from '@brightside/brightside-ui';
import {
  LoanPartner,
  LoanPayload,
  LoanPostStatusResponse,
  LoanStatus,
  NewLoansService,
} from '@brightside-web/desktop/feature/loans';
import { Subject, catchError, filter, map, of, takeUntil, tap } from 'rxjs';
import { LegalDocumentsService } from 'libs/shared/legal-documents/src/lib/legal-documents.service';
import { CurrencyPipe } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { routePath } from '@brightside-web/desktop/data-access/core-services';
import { animate, style, transition, trigger } from '@angular/animations';
import { ServiceInventoryService } from 'libs/desktop/data-access/service-inventory/src/lib/store/service-inventory.service';
import { ServiceState } from '@brightside-web/desktop/data-access/service-inventory';

enum RetryService {
  SERVICE_INVENTORY,
  GET_LOANS,
}

@Component({
    selector: 'brightside-web-loans-dashboard',
    templateUrl: './loans-dashboard-card.component.html',
    styleUrls: ['./loans-dashboard-card.component.scss'],
    imports: [BrightsideUiModule, TranslateModule],
    providers: [NewLoansService, LegalDocumentsService, CurrencyPipe],
    changeDetection: ChangeDetectionStrategy.OnPush,
    animations: [
        trigger('fadeIn', [transition(':enter', [style({ opacity: 0 }), animate('500ms ease-out', style({ opacity: 1 }))])]),
    ]
})
export class LoansDashboardCardComponent implements OnInit, OnDestroy {
  data: { vendor: string | undefined };
  showRetryAccessory: boolean = false;
  loanCellConfig: HeaderLabelCellConfig;

  private _router: Router = inject(Router);
  private _loansService: NewLoansService = inject(NewLoansService);
  private _currencyPipe: CurrencyPipe = inject(CurrencyPipe);
  private _translateService: TranslateService = inject(TranslateService);
  private _serviceInventory: ServiceInventoryService = inject(ServiceInventoryService);
  private _cdr: ChangeDetectorRef = inject(ChangeDetectorRef);
  private _partnerName: string;
  private _serviceToRetry: RetryService;
  private _ubsub$: Subject<void> = new Subject<void>();

  ngOnInit(): void {
    this.setLoanDashboardCardLoading();
    this.initializeLoansDashboardCard();

    this._serviceInventory
      .getService('loan_dashboard')
      .pipe(
        filter((service) => !!service),
        tap((service) => {
          switch (service.state) {
            case ServiceState.INITIAL:
            case ServiceState.PENDING:
              this.data = { vendor : service.route }
              this.initializeLoansDashboardCard();
              break;
            case ServiceState.MAINTENANCE:
              break;
          }
        }),
        takeUntil(this._ubsub$)
      )
      .subscribe();
  }

  getLoansInfoByPartner(): void {
    this._loansService
      .getLoanByPartnerType(this.data?.vendor)
      .pipe(
        map((res) => (res as LoanPayload)?.payload),
        tap((res) => {
          this.handleSuccessResponse(res);
        }),
        catchError(() => of(this.handleErrorResponse()))
      )
      .subscribe();
  }

  retryLoans() {
    this.showRetryAccessory = false;
    this.setLoanDashboardCardLoading();
    setTimeout(() => {
      if (this._serviceToRetry === RetryService.GET_LOANS) {
        this.getLoansInfoByPartner();
      } else if (this._serviceToRetry === RetryService.SERVICE_INVENTORY) {
        this._serviceInventory.loadInventory();
      }
    }, 3000);
  }

  navigateToLoanDashboard(): void {
    if (this._partnerName) {
      this._router.navigate([routePath('loan_dashboard')], { queryParams: { vendor: this._partnerName } });
    }
  }

  ngOnDestroy(): void {
    this._ubsub$.next();
  }

  private initializeLoansDashboardCard() : void {
    if (!this.data?.vendor) {
      this.showRetryAccessoryCard(RetryService.SERVICE_INVENTORY);
    } else {
      this.getLoansInfoByPartner();
    }
  }

  private handleSuccessResponse(data: { loans: LoanPostStatusResponse[]; partner: LoanPartner }) {
    const partner = data.partner;
    const loan = data.loans?.filter((loan) => loan.isTaken)[0];
    this._partnerName = partner.displayName;

    if (loan && partner) {
      this.setLoanDashboardCardInfo(
        partner.displayName,
        loan.remainingBalance,
        loan.paymentAmount,
        loan.status === LoanStatus.LOAN_PAID_OFF
      );
    }
  }

  private setLoanDashboardCardInfo(
    displayName: string,
    paymentAmount: number,
    remainingBalance: number,
    isRepaid: boolean
  ): void {
    this.loanCellConfig = {
      leftTitle: isRepaid ? this._translateService.instant('REPAID') : displayName,
      leftTitleSemibold: true,
      rightIconName: 'chevron_right',
      rightIconColor: 'var(--blue-50)',
      rightSubtitle: this._translateService.instant('LOAN_PAYMENT_AMOUNT', {
        amount: this._currencyPipe.transform(paymentAmount),
      }),
      rightTitle: this._currencyPipe.transform(remainingBalance),
      rightTitleSemibold: true,
      rightSubtitleNoWrap: true,      
      bgColor: 'var(--white)',
    };
    this.showRetryAccessory = false;
    this._cdr.detectChanges();
  }

  private setLoanDashboardCardLoading(): void {
    this.loanCellConfig = {
      leftTitle: this._translateService.instant('LOAN'),
      leftTitleSemibold: true,      
      bgColor: 'var(--white)',
      lottieLoading: true,
      minHeight: '2.2rem',
    };
  }

  private showRetryAccessoryCard(serviceToRetry: RetryService): void {
    this.loanCellConfig = {
      leftTitle: this._translateService.instant('LOAN'),
      leftTitleSemibold: true,
      bgColor: 'var(--white)',      
      minHeight: '2.2rem',
    };
    this._serviceToRetry = serviceToRetry;
    this.showRetryAccessory = true;
    this._cdr.detectChanges();
  }

  private handleErrorResponse() {
    this.showRetryAccessoryCard(RetryService.GET_LOANS);
  }
}
